import { Handle, Position } from "@xyflow/react";
import {
  Cloud,
  Database,
  HardDrive,
  Mail,
  Plug,
  Server,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import { useState } from "react";
import {
  externalSystemMatchesOverlay,
  type OverlayMode,
  overlayRingClass,
} from "@/lib/arch-overlay";
import type { ExternalSystem } from "@/lib/arch-parser";
import { cn } from "@/lib/utils";
import { useArchViz } from "../ArchVizContext";
import { brandIconSlug, brandIconUrl } from "./brand-icons";
import { CommentBadge } from "./CommentBadge";
import { kindPalette } from "./node-colors";
import { RiskBadge } from "./RiskBadge";

// v0.4 drill-state passed from use-graph-elements. The node uses it for a
// cursor change + appends `drillTitle` to the existing compact-mode tooltip
// content. See computeExternalDrillHint() in src/hooks/use-graph-elements.ts
// for the state derivation and copy.
export type ExternalDrillState = "drillable" | "non-drillable-modern" | "non-drillable-legacy";

export interface ExternalSystemNodeData {
  system: ExternalSystem;
  faded?: boolean;
  highlighted?: boolean;
  activeOverlay?: OverlayMode;
  commentCount?: number;
  carryOverCount?: number;
  compact?: boolean;
  drillState?: ExternalDrillState;
  drillTitle?: string;
  [key: string]: unknown;
}

function iconFor(system: ExternalSystem) {
  // Prefer the canonical `kind` (v0.3); fall back to legacy `type` so old
  // docs still get sensible icons. Lower-case both for substring checks.
  const kind = (system.kind ?? system.type ?? "").toLowerCase();
  const id = system.id.toLowerCase();
  const label = system.label.toLowerCase();

  if (id.includes("mail") || label.includes("mail") || label.includes("sendgrid")) return Mail;
  if (id.includes("s3") || label.includes("storage") || label.includes("blob")) return HardDrive;
  if (kind.includes("database") || label.includes("postgres") || label.includes("mysql")) {
    return Database;
  }
  if (kind === "auth-provider" || kind.includes("auth") || label.includes("okta")) {
    return ShieldCheck;
  }
  if (kind.includes("queue") || kind.includes("worker") || label.includes("queue")) return Workflow;
  if (kind === "saas") return Cloud;
  if (kind === "integration") return Plug;
  if (kind === "data-source") return Database;
  if (kind === "infrastructure") return Server;
  return Server;
}

export function ExternalSystemNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: ExternalSystemNodeData;
  selected?: boolean;
}) {
  const { state, dispatch } = useArchViz();
  const palette = kindPalette("external-system");
  const Icon = iconFor(data.system);
  const [brandFailed, setBrandFailed] = useState(false);
  const slug = brandFailed ? null : brandIconSlug(data.system);
  const overlay = data.activeOverlay ?? "none";
  const overlayMatch = overlay !== "none" && externalSystemMatchesOverlay(data.system, overlay);
  const riskCount = data.system.risks?.length ?? data.system.risk.length;
  const headline = data.system.headline ?? null;
  // Skip when description duplicates headline — defensive against fixtures
  // where the same string is set on both fields.
  const description =
    data.system.description && data.system.description !== data.system.headline
      ? data.system.description
      : null;
  const compactTooltipText =
    headline && description ? `${headline} — ${description}` : (headline ?? description);
  const kindLabel = data.system.kind ?? data.system.type;
  // v0.4 drill hint. Always surfaced on hover when present so users can tell
  // the three drill states apart. When compact, append headline/description
  // info so we don't lose the existing compact-mode tooltip.
  const titleParts: string[] = [];
  if (data.drillTitle) titleParts.push(data.drillTitle);
  if (data.compact && compactTooltipText) titleParts.push(compactTooltipText);
  const titleText = titleParts.length > 0 ? titleParts.join("\n\n") : undefined;
  const drillable = data.drillState === "drillable";
  return (
    <div
      className={cn(
        // Fixed height + flex column so cards stay uniform regardless of
        // label wrap count — keeps cluster edges straight (gap #6/9.3).
        // Width band matches EXTERNAL_NODE_SIZE_*.width in use-graph-elements.ts.
        // `border-dashed` is supplied by `palette.border`.
        "relative rounded-lg border-[1.5px] px-3 py-2.5 shadow-sm transition-all min-w-[200px] max-w-[240px] text-center flex flex-col",
        data.compact ? "h-[140px]" : "h-[200px]",
        palette.bg,
        palette.border,
        palette.text,
        selected &&
          "ring-2 ring-offset-2 ring-offset-background ring-[color:var(--node-selection-ring)] shadow-md z-10",
        data.faded && "opacity-30",
        data.highlighted && "ring-2 ring-teal-400/70",
        overlayMatch && overlay !== "none" && overlayRingClass(overlay),
        drillable && "cursor-pointer",
      )}
      title={titleText}
    >
      <div
        className="absolute -top-3.5 left-2 uppercase tracking-eyebrow font-semibold text-muted-foreground whitespace-nowrap pointer-events-none"
        style={{ fontSize: "calc(9px * var(--archviz-label-scale, 1))" }}
      >
        External system{kindLabel ? ` · ${kindLabel}` : ""}
      </div>
      <CommentBadge count={data.commentCount ?? 0} carryOver={data.carryOverCount ?? 0} />
      {state.showRisks && !data.compact && (
        <RiskBadge
          count={riskCount}
          onClick={() => dispatch({ type: "SELECT_NODE_FOR_RISKS", nodeId: id })}
        />
      )}
      <Handle type="target" position={Position.Top} id="top" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Left} id="left" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Right} id="right" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Bottom} id="bottom" className="!bg-foreground/30" />
      <div className="h-8 flex items-center justify-center mb-1">
        {slug ? (
          <img
            src={brandIconUrl(slug)}
            alt=""
            className="h-7 w-7 object-contain"
            onError={() => setBrandFailed(true)}
          />
        ) : (
          <Icon className="h-6 w-6 opacity-80" />
        )}
      </div>
      <div
        className="font-semibold leading-none tracking-tight"
        style={{ fontSize: "calc(14px * var(--archviz-label-scale, 1))" }}
      >
        {data.system.label}
      </div>
      {!data.compact && (headline || description) && (
        // Headline + description are clamped so the rendered card stays
        // inside its declared h-[200px] (EXTERNAL_NODE_SIZE_EXPANDED.height)
        // — long copy used to overflow and visually bleed into the next
        // external in the right-column stack.
        <div className="mt-0.5 space-y-0.5">
          {headline && (
            <div
              className="text-muted-foreground leading-snug line-clamp-2"
              style={{ fontSize: "calc(10px * var(--archviz-label-scale, 1))" }}
            >
              {headline}
            </div>
          )}
          {description && (
            <div
              className="text-muted-foreground/70 leading-snug line-clamp-4"
              style={{ fontSize: "calc(9px * var(--archviz-label-scale, 1))" }}
            >
              {description}
            </div>
          )}
        </div>
      )}
      <Handle type="source" position={Position.Top} id="top-src" className="!bg-foreground/30" />
      <Handle type="source" position={Position.Left} id="left-src" className="!bg-foreground/30" />
      <Handle
        type="source"
        position={Position.Right}
        id="right-src"
        className="!bg-foreground/30"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-src"
        className="!bg-foreground/30"
      />
    </div>
  );
}
